import {z} from 'zod';

import {IsoUtcDateTimeSchema} from './common.js';
import {
  CreateEditorialCycleRequestSchema,
  CreateSourceRequestSchema,
  CreateTopicRequestSchema,
  EvidenceRequestShapeSchema,
  TopicFormatSchema,
} from './editorial.js';

/**
 * O arquivo de semeadura editorial.
 *
 * É a entrada do CLI `semear-editorial`, que prepara um banco vazio para a
 * PRIMEIRA geração real: sem ciclo a abertura do dia responde
 * `422 NO_ACTIVE_CYCLE`, e sem pauta com prova cada peça falha com
 * `NO_ELIGIBLE_TOPIC` ou `TOPIC_WITHOUT_EVIDENCE`.
 *
 * ## Por que o envelope é português e o miolo é inglês
 *
 * O envelope (`ciclo`, `fontes`, `chave`, `evidencias`, `pautas`) é o que o
 * dono escreve à mão, e escrever em português é o que faz ele conferir o
 * arquivo em vez de copiar. Já `ciclo`, `fonte` e `pauta` carregam DENTRO deles
 * exatamente o corpo que as rotas `POST /v1/editorial/cycles`, `POST /v1/sources`
 * e o `createTopic` do repositório recebem, reaproveitando os mesmos schemas.
 *
 * Aninhar em vez de achatar é o que garante isso: um objeto achatado exigiria
 * copiar campo a campo, e a cópia divergiria no dia em que o contrato ganhasse
 * um campo. Aqui o arquivo não tem como divergir da rota, porque é o MESMO
 * schema.
 *
 * ## O que este arquivo NÃO faz, de propósito
 *
 * - **Não cadastra caso identificado.** `identified_case` exige autorização
 *   assinada do cliente, que é documento inspecionado (`POST
 *   /v1/evidence-objects` mais `POST /v1/client-authorizations`). Um arquivo de
 *   texto não carrega o PDF nem o consentimento, então a combinação é recusada
 *   aqui, com mensagem que diz por onde ela entra.
 * - **Não cadastra fonte por arquivo.** `uploaded_file` aponta para um objeto
 *   que ESTE servidor inspecionou; um arquivo de semeadura escolheria o
 *   identificador na mão, que é justamente o que a inspeção existe para impedir.
 *   Só `kind: 'url'` passa.
 *
 * As duas recusas são de lista de PERMISSÃO: o que não está previsto não passa
 * calado.
 */

/**
 * A chave local da fonte.
 *
 * Ela só existe dentro do arquivo, para a pauta apontar para a fonte antes de
 * qualquer uma das duas ter identificador no banco. O formato é fechado porque
 * chave com espaço ou acento vira erro de digitação silencioso na hora de
 * casar `pautas[].fontes` com `fontes[].chave`.
 */
export const SeedSourceKeySchema = z
  .string()
  .regex(
    /^[a-z0-9][a-z0-9._-]{1,39}$/u,
    'a chave usa de 2 a 40 caracteres, entre minúsculas, dígitos, ponto, hífen e sublinhado',
  );

/**
 * A evidência como o arquivo a declara.
 *
 * `sourceId` sai porque a fonte ainda não existe quando o arquivo é escrito, e
 * `clientAuthorizationId` sai porque este arquivo não cadastra caso
 * identificado. O que o arquivo declara é o RESUMO e o TIPO DE PROVA; o resto
 * do snapshot (o hash do que foi lido, o instante da consulta, a validade do
 * radar) quem descobre é `createEvidenceSnapshot`, buscando a URL na hora.
 */
export const SeedEvidenceSchema = EvidenceRequestShapeSchema.omit({
  sourceId: true,
  clientAuthorizationId: true,
}).refine((evidencia) => evidencia.proofType !== 'identified_case', {
  message:
    'caso identificado exige autorização assinada do cliente, que entra por ' +
    'POST /v1/evidence-objects e POST /v1/client-authorizations, e não por este arquivo',
  path: ['proofType'],
});

export const SeedSourceSchema = z.strictObject({
  chave: SeedSourceKeySchema,
  /** O corpo de `POST /v1/sources`, sem uma vírgula de diferença. */
  fonte: CreateSourceRequestSchema.refine((fonte) => fonte.kind === 'url', {
    message:
      'este arquivo só cadastra fonte do tipo url: fonte por arquivo depende ' +
      'de objeto inspecionado por este servidor',
    path: ['kind'],
  }),
  /**
   * Pelo menos uma. Fonte sem evidência não sustenta pauta nenhuma: a esteira
   * recusa com `TOPIC_WITHOUT_EVIDENCE` antes de gastar geração, e uma fonte
   * cadastrada sozinha só engorda o banco.
   */
  evidencias: z.array(SeedEvidenceSchema).min(1),
});

export const SeedTopicSchema = z
  .strictObject({
    /** O corpo que `createTopic` recebe, menos a validade. */
    pauta: CreateTopicRequestSchema,
    /** Chaves de `fontes`. Pauta sem prova é pauta que a esteira recusa. */
    fontes: z.array(SeedSourceKeySchema).min(1),
    /**
     * Para qual peça a pauta serve. Ausente é `reel`, que é tudo que existia
     * antes de 2026-09-04.
     *
     * Ele mora AQUI, no envelope, e não dentro de `pauta`, pelo mesmo motivo de
     * `expiraEm`: `CreateTopicRequestSchema` é o corpo de uma rota, e a rota de
     * pauta não existe. Quem grava é `createTopic`, que recebe o formato
     * separado. Aceitar nos dois lugares daria duas formas de dizer a mesma
     * coisa no mesmo arquivo.
     *
     * `default` e não `optional`: o resto do código recebe sempre um valor, e
     * nunca precisa perguntar se o campo veio.
     */
    formato: TopicFormatSchema.default('reel'),
    /**
     * A validade da pauta de radar.
     *
     * Ela não está em `CreateTopicRequestSchema` porque a rota de pauta não
     * existe; quem grava é `createTopic`, que recebe `expiresAt` separado. O
     * banco tem a mesma cerca em `topics_radar_needs_expiry`.
     */
    expiraEm: IsoUtcDateTimeSchema.nullable(),
  })
  .refine(
    (entrada) =>
      entrada.pauta.sourceMode !== 'radar' || entrada.expiraEm !== null,
    {
      message: 'pauta de radar precisa de data de validade em expiraEm',
      path: ['expiraEm'],
    },
  )
  .refine(
    (entrada) =>
      entrada.pauta.sourceMode !== 'durable' || entrada.expiraEm === null,
    {
      // Durável com validade seria uma pauta que some do inventário sem que
      // ninguém tenha decidido isso. `null` é a única forma de dizer "não
      // vence", e a ausência do campo não serve: ela é a mesma coisa que
      // esquecer de preencher.
      message: 'pauta durável não vence: use expiraEm nulo',
      path: ['expiraEm'],
    },
  )
  .refine(
    (entrada) =>
      entrada.formato !== 'news' || entrada.pauta.sourceMode === 'radar',
    {
      // Mesma cerca que `topics_news_is_radar` tem no banco, e ela está nos dois
      // lugares de propósito: aqui ela recusa o ARQUIVO, com o caminho do campo,
      // antes de qualquer captura de fonte custar rede; lá ela recusa a linha,
      // que é o que vale contra qualquer outro caminho de escrita. Notícia
      // durável seria notícia que não envelhece, disputando o item do dia com
      // as notícias de verdade para sempre.
      message: 'pauta de formato news é sempre radar: use sourceMode "radar"',
      path: ['pauta', 'sourceMode'],
    },
  );

export const EditorialSeedFileSchema = z
  .strictObject({
    ciclo: CreateEditorialCycleRequestSchema,
    fontes: z.array(SeedSourceSchema).min(1),
    pautas: z.array(SeedTopicSchema).min(1),
  })
  .refine(
    (arquivo) =>
      new Set(arquivo.fontes.map((fonte) => fonte.chave)).size ===
      arquivo.fontes.length,
    {
      message: 'duas fontes com a mesma chave: a segunda apagaria a primeira',
      path: ['fontes'],
    },
  )
  .superRefine((arquivo, ctx) => {
    // Chave que não existe é erro de digitação, e ele precisa aparecer AQUI:
    // gravado, viraria pauta sem fonte vinculada, que a esteira só recusa no
    // dia da geração, com `TOPIC_WITHOUT_EVIDENCE`.
    const conhecidas = new Set(arquivo.fontes.map((fonte) => fonte.chave));
    for (const [indice, entrada] of arquivo.pautas.entries()) {
      for (const [posicao, chave] of entrada.fontes.entries()) {
        if (!conhecidas.has(chave)) {
          ctx.addIssue({
            code: 'custom',
            message: `a pauta aponta para a fonte "${chave}", que não está em fontes`,
            path: ['pautas', indice, 'fontes', posicao],
          });
        }
      }
    }
  });

export type EditorialSeedFile = z.infer<typeof EditorialSeedFileSchema>;
export type SeedSource = EditorialSeedFile['fontes'][number];
export type SeedTopic = EditorialSeedFile['pautas'][number];
export type SeedEvidence = SeedSource['evidencias'][number];
